import React, { useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { DELETE_TRANSACTION } from '../graphql/mutations/transactions';
import { formatCurrency } from '../utils/formatters';
import { TransactionType } from '../utils/enums';
import { removeTransactionFromCache } from '../utils/apolloCache';
import toast from 'react-hot-toast';

const DeleteTransactionModal = ({ isOpen, onClose, transaction, updateCache, portfolioId, refetch, onDeleted }) => {
  const [deleteTransaction, { loading }] = useMutation(DELETE_TRANSACTION);
  const modalRef = useRef(null);
  const deleteButtonRef = useRef(null);

  // Focus delete button when modal opens
  useEffect(() => {
    if (isOpen && deleteButtonRef.current) {
      deleteButtonRef.current.focus();
    }
  }, [isOpen]);

  // Add event listener for ESC key press
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  
  // Backdrop click handler removed to prevent accidental closure

  const handleDelete = async () => {
    if (!transaction) return;
    
    try {
      const response = await deleteTransaction({
        variables: {
          transactionId: transaction.id,
          portfolioId: portfolioId
        }
      });
      
      // Check for errors
      if (!response.data?.deleteTransaction?.success) {
        const errorMessage = response.data?.deleteTransaction?.error || 'Failed to delete transaction';
        console.error('Delete transaction failed:', errorMessage);
        
        // Show error toast
        toast.error(`Failed to delete transaction: ${errorMessage}`);
        return;
      }
      
      // Remove transaction from cache only if deletion was successful
      updateCache((cache, variables) => {
        removeTransactionFromCache(cache, variables, transaction.id);
      });
      
      // Show success toast
      toast.success('Transaction deleted successfully');
      
      // Inform parent to update UI immediately
      try {
        if (typeof onDeleted === 'function') {
          onDeleted(transaction.id);
        }
      } catch (_) {}

      // Background refetch to refresh aggregates and also handle the case where position may disappear
      if (typeof refetch === 'function') {
        try {
          refetch();
        } catch (_) {}
      }
      
      onClose();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      // Show error toast
      toast.error(`Error deleting transaction: ${error.message}`);
    }
  };

  if (!isOpen || !transaction) return null;

  // Normalize transaction type to always use uppercase enum format
  const txType = transaction.transactionType?.toUpperCase?.() || transaction.transactionType;

  // Format price display based on transaction type
  const priceDisplay = TransactionType.isTransfer(txType) || txType === TransactionType.SPLIT
    ? '-'
    : formatCurrency(transaction.price?.toString() || '0');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div ref={modalRef} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Delete Transaction
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </p>
          <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
            <p><span className="font-semibold">Type:</span> {TransactionType.getDisplayName(txType)}</p>
            <p><span className="font-semibold">{txType === TransactionType.SPLIT ? 'Split ratio:' : 'Quantity:'}</span> {txType === TransactionType.SPLIT ? '×' : ''}{transaction.quantity}</p>
            <p><span className="font-semibold">Price:</span> {priceDisplay}</p>
            <p><span className="font-semibold">Date:</span> {new Date(transaction.timestamp).toLocaleString()}</p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="mr-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-1 focus:ring-red-400"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteTransactionModal; 